import type { ContactMethod } from '@/features/contact/Contact'
import { executeStep, isVisibleStep, shouldRenderStep } from '@/flow/engine'
import { buildFlowForObjectives } from '@/flow/objectives'
import { baseUrl } from '@/lib/api/base-url'
import Client from '@/lib/api/client'
import { createAuthClient } from '@/lib/auth'
import type { FlowState, Objetivo } from '@/types'
import { create } from 'zustand'

// API client instance
const client = new Client(baseUrl)

type FlowStore = FlowState & {
  // Routing state
  selectedObjective: string
  selectedObjectives: Objetivo[]
  contactMethod: ContactMethod | null
  submissionState: 'idle' | 'submitting' | 'success' | 'error'
  submissionId: string | null
  // Actions
  setSelectedObjective: (id: string) => void
  setSelectedObjectives: (ids: Objetivo[]) => void
  toggleSelectedObjective: (id: Objetivo) => void
  setContactMethod: (method: ContactMethod) => void
  setSubmissionState: (state: FlowStore['submissionState']) => void
  setSubmissionId: (id: string) => void
  advance: () => void
  goBack: () => void
  setUserInput: (key: string, value: string | string[]) => void
  reset: () => void
  restart: (initialVars?: Record<string, unknown>) => void
  complete: () => void
  init: () => void
  resetAll: () => void
  submitChatData: () => Promise<{ success: boolean; id?: string }>
  submitChatAuthenticated: () => Promise<{ success: boolean; id?: string }>
  submitContactData: (contactValue: string) => Promise<{ success: boolean }>
}

const initialState: FlowState = {
  steps: [],
  index: 0,
  renderedSteps: [],
  context: { vars: {} },
  completed: false,
  typing: false,
}

function randomDelay(): number {
  return 1000 + Math.random() * 500
}

function normalizeObjectives(input: unknown): Objetivo[] {
  if (!Array.isArray(input)) return []

  const seen = new Set<string>()
  const objetivos: Objetivo[] = []

  for (const value of input) {
    if (typeof value !== 'string' || value.length === 0) continue
    if (seen.has(value)) continue
    seen.add(value)
    objetivos.push(value as Objetivo)
  }

  return objetivos
}

function objectivesFromVars(vars: Record<string, unknown>): Objetivo[] {
  const objetivos = normalizeObjectives(vars.objetivos)
  if (objetivos.length > 0) return objetivos

  if (typeof vars.objetivo === 'string' && vars.objetivo.length > 0) {
    return [vars.objetivo as Objetivo]
  }

  return []
}

function objectiveVars(objetivos: Objetivo[]): Record<string, unknown> {
  if (objetivos.length === 0) return {}
  return {
    objetivo: objetivos[0],
    objetivos: [...objetivos],
  }
}

let typingTimer: ReturnType<typeof setTimeout> | null = null

export const useFlowStore = create<FlowStore>((set, get) => ({
  ...initialState,
  selectedObjective: '',
  selectedObjectives: [],
  contactMethod: null,
  submissionState: 'idle',
  submissionId: null,

  setSelectedObjective: (id: string) =>
    set({
      selectedObjective: id,
      selectedObjectives: id ? [id as Objetivo] : [],
    }),
  setSelectedObjectives: (ids: Objetivo[]) => {
    const objetivos = normalizeObjectives(ids)
    set({
      selectedObjectives: objetivos,
      selectedObjective: objetivos[0] ?? '',
    })
  },
  toggleSelectedObjective: (id: Objetivo) =>
    set((state) => {
      const exists = state.selectedObjectives.includes(id)
      const selectedObjectives = exists
        ? state.selectedObjectives.filter((objectiveId) => objectiveId !== id)
        : [...state.selectedObjectives, id]

      return {
        selectedObjectives,
        selectedObjective: selectedObjectives[0] ?? '',
      }
    }),
  setContactMethod: (method: ContactMethod) => set({ contactMethod: method }),
  setSubmissionState: (state) => set({ submissionState: state }),
  setSubmissionId: (id: string) => set({ submissionId: id }),

  advance: () => {
    if (typingTimer) {
      clearTimeout(typingTimer)
      typingTimer = null
    }

    const { steps, index, context, renderedSteps } = get()

    let currentIndex = index
    const pendingSteps: typeof steps = []

    // Collect all steps until the next input/choice pause point
    while (currentIndex < steps.length) {
      const step = steps[currentIndex]

      if (!shouldRenderStep(step, context)) {
        currentIndex++
        continue
      }

      if (step.type === 'compute') {
        executeStep(step, context)
        currentIndex++
        continue
      }

      if (isVisibleStep(step)) {
        pendingSteps.push(step)
        currentIndex++

        if (step.type === 'input' || step.type === 'choice') {
          break
        }
      } else {
        currentIndex++
      }
    }

    const finalIndex = currentIndex
    const isComplete =
      finalIndex >= steps.length &&
      !pendingSteps.some((s) => s.type === 'input' || s.type === 'choice')

    // Reveal steps one by one with typing delays
    function revealNext(stepsLeft: typeof pendingSteps, currentRendered: typeof renderedSteps) {
      if (stepsLeft.length === 0) {
        set({
          index: finalIndex,
          renderedSteps: currentRendered,
          typing: false,
          completed: isComplete,
        })
        return
      }

      const [next, ...rest] = stepsLeft

      // All visible steps contain a bot message — show typing before revealing
      set({ typing: true })
      typingTimer = setTimeout(() => {
        typingTimer = null
        const updated = [...currentRendered, next]
        set({
          renderedSteps: updated,
          typing: false,
        })

        if (rest.length > 0) {
          typingTimer = setTimeout(() => {
            typingTimer = null
            revealNext(rest, updated)
          }, 150)
        } else {
          set({
            index: finalIndex,
            completed: isComplete,
          })
        }
      }, randomDelay())
    }

    revealNext(pendingSteps, [...renderedSteps])
  },

  setUserInput: (key: string, value: string | string[]) => {
    const { context } = get()
    context.vars[key] = value
    set({ context: { ...context } })
    // Let React render the user's message before starting typing
    setTimeout(() => get().advance(), 50)
  },

  goBack: () => {
    if (typingTimer) {
      clearTimeout(typingTimer)
      typingTimer = null
    }

    const { steps, renderedSteps, context } = get()

    // Find the last answered input/choice step in renderedSteps
    let targetRenderedIndex = -1
    for (let i = renderedSteps.length - 1; i >= 0; i--) {
      const step = renderedSteps[i]
      if (
        (step.type === 'input' || step.type === 'choice') &&
        step.inputKey &&
        context.vars[step.inputKey] !== undefined
      ) {
        targetRenderedIndex = i
        break
      }
    }

    if (targetRenderedIndex === -1) return

    const targetStep = renderedSteps[targetRenderedIndex]
    const targetStepIndex = steps.indexOf(targetStep)
    if (targetStepIndex === -1) return

    // Clear answers from the target step onwards
    for (let i = targetStepIndex; i < steps.length; i++) {
      const step = steps[i]
      if (step.inputKey && context.vars[step.inputKey] !== undefined) {
        delete context.vars[step.inputKey]
      }
    }

    // Keep renderedSteps up to and including the target step (question shows, answer cleared)
    const newRenderedSteps = renderedSteps.slice(0, targetRenderedIndex + 1)

    set({
      renderedSteps: newRenderedSteps,
      index: targetStepIndex + 1,
      context: { ...context },
      typing: false,
      completed: false,
    })
  },

  reset: () => {
    if (typingTimer) {
      clearTimeout(typingTimer)
      typingTimer = null
    }
    const objetivos = objectivesFromVars(get().context.vars)
    const steps = objetivos.length > 0 ? buildFlowForObjectives(objetivos) : []
    const freshContext = { vars: objectiveVars(objetivos) }
    set({
      ...initialState,
      steps,
      context: freshContext,
      selectedObjectives: objetivos,
      selectedObjective: objetivos[0] ?? '',
      contactMethod: null,
      submissionState: 'idle',
      submissionId: null,
    })
    setTimeout(() => get().advance(), 0)
  },

  restart: (initialVars?: Record<string, unknown>) => {
    if (typingTimer) {
      clearTimeout(typingTimer)
      typingTimer = null
    }
    const vars = { ...(initialVars ?? {}) }
    const objetivos = objectivesFromVars(vars)

    if (objetivos.length > 0) {
      vars.objetivo = objetivos[0]
      vars.objetivos = [...objetivos]
    } else {
      delete vars.objetivo
      delete vars.objetivos
    }

    const steps = objetivos.length > 0 ? buildFlowForObjectives(objetivos) : []
    const freshContext = { vars }
    set({
      ...initialState,
      steps,
      context: freshContext,
      selectedObjectives: objetivos,
      selectedObjective: objetivos[0] ?? '',
      contactMethod: null,
      submissionState: 'idle',
      submissionId: null,
    })
    setTimeout(() => get().advance(), 0)
  },

  complete: () => {
    set({ completed: true })
  },

  init: () => {
    const { index, renderedSteps, steps } = get()
    if (index === 0 && renderedSteps.length === 0 && steps.length > 0) {
      get().advance()
    }
  },

  resetAll: () => {
    if (typingTimer) {
      clearTimeout(typingTimer)
      typingTimer = null
    }
    set({
      ...initialState,
      selectedObjective: '',
      selectedObjectives: [],
      contactMethod: null,
      submissionState: 'idle',
      submissionId: null,
    })
  },

  submitChatData: async () => {
    const { context, submissionState } = get()
    if (submissionState !== 'idle') {
      return { success: submissionState === 'success', id: get().submissionId ?? undefined }
    }

    set({ submissionState: 'submitting' })

    try {
      // Unified flow: creates user + submission + sends magic link
      const result = await client.chat.submitChat({
        email: context.vars.email as string,
        name: context.vars.nome as string | undefined,
        data: context.vars,
        userAgent: navigator.userAgent,
      })

      if (result.success && result.submissionId) {
        set({ submissionState: 'success', submissionId: result.submissionId })
        return { success: true, id: result.submissionId }
      }
      set({ submissionState: 'error' })
      return { success: false }
    } catch (error) {
      console.error('Submit chat error:', error)
      set({ submissionState: 'error' })
      return { success: false }
    }
  },

  submitChatAuthenticated: async () => {
    const { context, submissionState } = get()
    if (submissionState !== 'idle') {
      return { success: submissionState === 'success', id: get().submissionId ?? undefined }
    }

    const authClient = createAuthClient()
    if (!authClient) {
      return { success: false }
    }

    set({ submissionState: 'submitting' })

    try {
      const result = await authClient.chat.submitChatAuthenticated({
        data: context.vars,
        userAgent: navigator.userAgent,
      })

      if (result.success && result.submissionId) {
        set({ submissionState: 'success', submissionId: result.submissionId })
        return { success: true, id: result.submissionId }
      }
      set({ submissionState: 'error' })
      return { success: false }
    } catch (error) {
      console.error('Submit authenticated chat error:', error)
      set({ submissionState: 'error' })
      return { success: false }
    }
  },

  submitContactData: async (contactValue: string) => {
    const { context } = get()
    // Save email to context before submitting
    context.vars.email = contactValue
    set({ context: { ...context }, contactMethod: 'email' })

    // Now submit all data including email
    const result = await get().submitChatData()
    return { success: result.success }
  },
}))
